import React, { FC, KeyboardEvent, useCallback, useContext, useRef, useState } from 'react';
import { IOptionBase, ISelectMenuPortal, OnChange } from '../../models';
import { SelectOption } from '../../_Option';
import { isEqual } from 'lodash';
import { Box } from '@src/atoms/Box';
import { Input } from '@src/atoms/Input';
import { Button } from '@src/atoms/Button';
import { Text } from '@src/atoms/Text';
import { Modal } from '@src/core/Modal';
import { classnames } from '@src/utils/classnames';
import { isInclude } from '@src/utils/string';

import styled from './MenuPortalSearchView.module.scss';
import { GroupedOptions } from './GroupedMenuPortalSearch';
import { getAllOptions, groupOptions } from '../../utils';
import { SelectContext } from '../../context';

interface IProps extends ISelectMenuPortal {}

export const MenuPortalSearchView: FC<IProps> = (props) => {
  const { isGroupedOptions } = useContext(SelectContext);
  const [value, setValue] = useState('');
  const [options, setOptions] = useState(props.options);
  const { selectProps, isMulti, getValue } = props;
  const { setMenuIsOpen, storage, onChange, classNamePrefix, getOptionLabel, getOptionValue } = selectProps;
  const classes = classnames(styled.root, classNamePrefix);
  const ref = useRef<HTMLInputElement>(null);
  const selectId = selectProps['data-id'];

  const onBackdropClick = useCallback(() => {
    setMenuIsOpen?.(false);
  }, [setMenuIsOpen]);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const targetValue = e.target.value;
      const allOptions = getAllOptions(props.options, isGroupedOptions);
      setValue(targetValue);
      const newOption = targetValue
        ? allOptions.filter((o) => o?.label && isInclude(String(o.label), String(targetValue)))
        : allOptions;

      if (isGroupedOptions) {
        const newGroupedOptions = groupOptions(newOption);
        setOptions(newGroupedOptions);
        return;
      }
      //@ts-ignore
      setOptions(newOption);
    },
    [props.options, isGroupedOptions],
  );

  /** Т.к. react-select где то перехватывает событие нажатия на backspace и не дает выполнять это инпуту по дефолту как onChange */
  const handleBackspace = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        //@ts-ignore
        const newValue: string = e.target.value || '';
        //@ts-ignore
        e.target.value = typeof newValue === 'string' ? newValue.slice(0, newValue.length - 1) : '';
        //@ts-ignore
        handleFilterChange(e);
      }
    },
    [handleFilterChange],
  );

  const handleChange = useCallback<OnChange>(
    (v, a) => {
      if (!isMulti) {
        onChange(v, a);
        return;
      }
      let values = getValue() as IOptionBase[];
      if (a.action === 'deselect-option') {
        values = values.filter((val) => !isEqual(val.value, v.value));
      } else {
        values.push(v);
      }

      onChange(values, a);
    },
    [getValue, isMulti, onChange],
  );

  return (
    <Modal open onBackdropClick={onBackdropClick}>
      <Box
        column
        bgColor="basicWhite"
        fullWidth
        className={classes}
        justifyContent="spaceBetween"
        data-id={`${selectId}_menuWrapperMobileSearch`}
      >
        <Box
          column
          //Todo: experiment
          style={{ display: 'contents' }}
        >
          <Box
            paddingBottom={{ MOBILE: 12 }}
            paddingTop={{ MOBILE: 12 }}
            paddingLeft={{ MOBILE: 16 }}
            paddingRight={{ MOBILE: 16 }}
            alignItems="center"
          >
            <Input
              id="SelectSearchInput"
              ref={ref}
              preIcon="search"
              onChange={handleFilterChange}
              value={value}
              onKeyDown={handleBackspace}
              placeholder="Поиск"
            />

            <Box>
              <Button onClick={onBackdropClick} view="textLight" id="cancelSearchBtn">
                <Text textDecoration="underline" id={'cancel'}>
                  Отмена
                </Text>
              </Button>
            </Box>
          </Box>
          {isGroupedOptions ? (
            <Box column marginTop={{ MOBILE: 4 }} className={styled.rootList}>
              {options.map((el) => (
                <GroupedOptions
                  key={el.label as string}
                  options={el}
                  selectProps={selectProps}
                  props={props}
                  handleChange={handleChange}
                />
              ))}
            </Box>
          ) : (
            <Box column marginTop={{ MOBILE: 4 }} className={styled.rootList}>
              {(options as unknown as IOptionBase[]).map((option) => (
                <SelectOption
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  selectId={props.selectProps['data-id']!}
                  key={option.value}
                  data={option}
                  onChange={handleChange}
                  storage={storage}
                  classNamePrefix={selectProps.classNamePrefix}
                  values={props.getValue() as IOptionBase[]}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isSearchView={true}
                />
              ))}
            </Box>
          )}
        </Box>

        {isMulti && (
          <Box paddingTop={20} paddingBottom={24} paddingLeft={16} paddingRight={16}>
            <Button fullWidth size="lg" id="submitBtn" onClick={onBackdropClick}>
              Готово
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};
